import { AppDispatch, ThunkExtraArguments } from "../store";
import { setRetailerNameMap } from "../reducers/retailerNameMap";
import { setRetailers } from "../reducers/retailers";
import { getRetailersAuth, getSyncEnabledRetailersAuth, getCheckoutEnabledRetailersAuth } from "../../api/graphQl/authenticated";
import { getRetailers } from "../../api";
import { EligibilityStatus, Retailer, RetailerWithSyncStatus } from "../types";

const buildRetailerNameMap = (retailers: Retailer[]): Record<number, string> =>
  Object.fromEntries(
    retailers.map((r) => [r.retailerId, r.displayName || r.name])
  );

/* --------------------- retailers ---------------------*/

interface AuthRetailerResponse {
  Retailers: RetailerWithSyncStatus[];
}
interface UnauthRetailerResponse {
  RetailersForLandingPage: RetailerWithSyncStatus[];
}
export const loadRetailersAuth =
  () =>
  async (
    dispatch: AppDispatch,
    _getState: unknown,
    { apolloClient }: ThunkExtraArguments
  ): Promise<RetailerWithSyncStatus[] | null> => {
    try {
      const { data } = await apolloClient.query<AuthRetailerResponse>({
        query: getRetailersAuth,
        variables: { enableExperimentalRetailers: true },
        fetchPolicy: "no-cache",
      });
      if (data?.Retailers?.length > 0) {
        dispatch(setRetailerNameMap(buildRetailerNameMap(data.Retailers)));
        const allRetailers = data.Retailers.map((retailer) => ({
          ...retailer,
          parentRetailerId: retailer?.parentRetailerId ?? retailer.retailerId,
        }));
        dispatch(setRetailers(allRetailers));
        return allRetailers;
      }
    } catch (e) {
      console.error("loadRetailersAuth error: ", e);
    }
    return null;
  };

export const loadRetailersUnauth =
  () =>
  async (
    dispatch: AppDispatch,
    _getState: unknown,
    { apolloClient }: ThunkExtraArguments
  ): Promise<RetailerWithSyncStatus[] | null> => {
    try {
      const { data } = await apolloClient.query<UnauthRetailerResponse>({
        query: getRetailers,
        variables: { enableExperimentalRetailers: true },
        fetchPolicy: "cache-first",
      });
      if (data?.RetailersForLandingPage) {
        dispatch(setRetailerNameMap(buildRetailerNameMap(data.RetailersForLandingPage)));
      }
      const serverSideEnabledRetailers = data?.RetailersForLandingPage.filter(
        (retailer) => {
          return retailer.syncConfig.serverSide === EligibilityStatus.ENABLED;
        }
      );
      dispatch(setRetailers(serverSideEnabledRetailers));
      return serverSideEnabledRetailers;
    } catch (e) {
      console.error("loadRetailersUnauth error: ", e);
    }
    return null;
  };

interface SyncEnabledRetailersResponse {
  SyncEnabledRetailers: RetailerWithSyncStatus[];
}

export const loadSyncEnabledRetailersAuth =
  () =>
  async (
    dispatch: AppDispatch,
    _getState: unknown,
    { apolloClient }: ThunkExtraArguments
  ): Promise<RetailerWithSyncStatus[] | null> => {
    try {
      const { data } = await apolloClient.query<SyncEnabledRetailersResponse>({
        query: getSyncEnabledRetailersAuth,
        variables: { enableExperimentalRetailers: true },
        fetchPolicy: "no-cache",
      });
      if (data?.SyncEnabledRetailers?.length > 0) {
        const allRetailers = data.SyncEnabledRetailers.map((retailer) => ({
          ...retailer,
          parentRetailerId: retailer?.parentRetailerId ?? retailer.retailerId,
        }));
        dispatch(setRetailers(allRetailers));
        return allRetailers;
      }
    } catch (e) {
      console.error("loadSyncEnabledRetailersAuth error: ", e);
    }
    return null;
  };

interface CheckoutEnabledRetailersResponse {
  CheckoutEnabledRetailers: RetailerWithSyncStatus[];
}

export const loadCheckoutEnabledRetailersAuth =
  () =>
  async (
    dispatch: AppDispatch,
    _getState: unknown,
    { apolloClient }: ThunkExtraArguments
  ): Promise<RetailerWithSyncStatus[] | null> => {
    try {
      const { data } = await apolloClient.query<CheckoutEnabledRetailersResponse>({
        query: getCheckoutEnabledRetailersAuth,
        variables: { enableExperimentalRetailers: true },
        fetchPolicy: "no-cache",
      });
      if (data?.CheckoutEnabledRetailers?.length > 0) {
        const allRetailers = data.CheckoutEnabledRetailers.map((retailer) => ({
          ...retailer,
          parentRetailerId: retailer?.parentRetailerId ?? retailer.retailerId,
        }));
        dispatch(setRetailers(allRetailers));
        return allRetailers;
      }
    } catch (e) {
      console.error("loadCheckoutEnabledRetailersAuth error: ", e);
    }
    return null;
  };
